import { useCallback, useEffect, useRef, useState } from 'react';
import { trimSignatureCanvas } from '@/lib/signature-canvas-trim';

interface ContractSignaturePadProps {
  onChange: (signatureData: string | null) => void;
  disabled?: boolean;
  layout?: 'default' | 'contratovista';
}

export function ContractSignaturePad({
  onChange,
  disabled = false,
  layout = 'default',
}: ContractSignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const [hasStroke, setHasStroke] = useState(false);

  const readSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStroke) return null;
    return trimSignatureCanvas(canvas) ?? canvas.toDataURL('image/png');
  }, [hasStroke]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sizeSource =
      layout === 'contratovista' ? wrapRef.current : canvas;
    if (!sizeSource) return;

    const width = Math.max(1, sizeSource.clientWidth);
    const height = Math.max(1, sizeSource.clientHeight);

    canvas.width = width;
    canvas.height = height;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [layout]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    resizeCanvas();

    if (layout !== 'contratovista') {
      return;
    }

    const wrap = wrapRef.current;
    if (!wrap) return;

    const observer = new ResizeObserver(() => {
      setHasStroke(false);
      onChange(null);
      resizeCanvas();
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [layout, resizeCanvas]);

  function getPoint(event: PointerEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return { x: 0, y: 0 };
    }

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  function startDraw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    drawingRef.current = true;
    canvas.setPointerCapture(event.pointerId);
    const point = getPoint(event.nativeEvent, canvas);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }

  function draw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const point = getPoint(event.nativeEvent, canvas);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    if (!hasStroke) setHasStroke(true);
  }

  function endDraw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    onChange(readSignature());
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
    onChange(null);
  }

  if (layout === 'contratovista') {
    return (
      <div className="contract-signature-pad contract-signature-pad--contratovista">
        <div className="mb-4">
          <h3 className="contract-signature-pad__heading">
            <span className="material-symbols-outlined" aria-hidden="true">draw</span>
            Firma Digital
          </h3>
          <p className="contract-signature-pad__hint">
            Dibuje su firma en el recuadro inferior.
          </p>
        </div>
        <div ref={wrapRef} className="contract-signature-pad__canvas-wrap">
          {!hasStroke ? (
            <span className="contract-signature-pad__placeholder">Firme aquí</span>
          ) : null}
          <canvas
            ref={canvasRef}
            className="contract-signature-pad__canvas contract-signature-pad__canvas--contratovista"
            aria-label="Área de firma"
            onPointerDown={startDraw}
            onPointerMove={draw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
          />
        </div>
        <div className="contract-signature-pad__toolbar">
          <button
            type="button"
            className="contract-signature-pad__clear contract-signature-pad__clear--contratovista"
            onClick={clear}
            disabled={disabled || !hasStroke}
          >
            <span className="material-symbols-outlined" aria-hidden="true">delete</span>
            Limpiar firma
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contract-signature-pad">
      <p className="contract-signature-pad__label">Firma del estudiante</p>
      <p className="contract-signature-pad__hint">
        Dibuja tu firma en el recuadro con el mouse o el dedo.
      </p>
      <canvas
        ref={canvasRef}
        className="contract-signature-pad__canvas"
        aria-label="Área de firma"
        onPointerDown={startDraw}
        onPointerMove={draw}
        onPointerUp={endDraw}
        onPointerLeave={endDraw}
      />
      <button
        type="button"
        className="contract-signature-pad__clear"
        onClick={clear}
        disabled={disabled || !hasStroke}
      >
        Limpiar firma
      </button>
    </div>
  );
}
